/** Base URL for the self-hosted OXN Sourcify server (API v1). */
export const getSourcifyServerUrl = (): string => {
  const configured = import.meta.env.REACT_APP_SOURCIFY_SERVER_URL
  if (configured) return configured.replace(/\/$/, '')
  // Proxied by explorer nginx in production.
  return '/sourcify'
}

export const getSourcifyChainId = (): string => String(import.meta.env.REACT_APP_EVM_CHAIN_ID || 186)

export type SourcifyCheckStatus = 'perfect' | 'partial' | false | string

/** Check whether an address is verified on our Sourcify instance. */
export async function checkSourcifyVerification(
  address_eth: string,
): Promise<'full' | 'partial' | undefined> {
  const url = `${getSourcifyServerUrl()}/check-by-addresses?addresses=${encodeURIComponent(
    address_eth,
  )}&chainIds=${encodeURIComponent(getSourcifyChainId())}`
  const response = await fetch(url)
  if (!response.ok) return undefined
  const data = (await response.json()) as Array<{ address?: string; status?: SourcifyCheckStatus }>
  const row = data.find(item => item.address?.toLowerCase() === address_eth.toLowerCase()) ?? data[0]
  if (!row || row.status === false || row.status === 'false') return undefined
  if (row.status === 'partial') return 'partial'
  if (row.status === 'perfect' || row.status === 'full') return 'full'
  // Some responses use "perfect" aliases; treat any truthy match as full.
  if (row.status) return 'full'
  return undefined
}

export type SourcifySourceFile = {
  name: string
  path: string
  content: string
}

export type SourcifySourcesResult = {
  verificationLevel: 'full' | 'partial'
  sourceFiles: SourcifySourceFile[]
  compilationMetadata?: Record<string, unknown>
}

/** Fetch verified source files from Sourcify (`/files/any/:chain/:address`). */
export async function fetchSourcifySources(
  address_eth: string,
): Promise<SourcifySourcesResult | undefined> {
  const url = `${getSourcifyServerUrl()}/files/any/${encodeURIComponent(
    getSourcifyChainId(),
  )}/${encodeURIComponent(address_eth)}`
  const response = await fetch(url)
  if (!response.ok) return undefined
  const data = (await response.json()) as {
    status?: string
    files?: Array<{ name?: string; path?: string; content?: string }>
  }
  if (!data.files?.length) return undefined

  const sourceFiles: SourcifySourceFile[] = data.files
    .filter(f => f.content != null && f.name)
    .map(f => ({
      name: f.name!,
      path: f.path || f.name!,
      content: f.content!,
    }))

  const metadataFile = sourceFiles.find(f => f.name === 'metadata.json')
  let compilationMetadata: Record<string, unknown> | undefined
  if (metadataFile) {
    try {
      compilationMetadata = JSON.parse(metadataFile.content) as Record<string, unknown>
    } catch {
      compilationMetadata = undefined
    }
  }

  const verificationLevel: 'full' | 'partial' =
    data.status === 'partial' || data.status === 'partial_match' ? 'partial' : 'full'

  return {
    verificationLevel,
    sourceFiles: sourceFiles.filter(f => f.name !== 'metadata.json'),
    compilationMetadata,
  }
}
